import { useNavigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, Users, Rocket, Coins, MessageSquare, Sparkles, Quote } from "lucide-react";

const DO = [
  "Generate VR lessons in small teams using VividVR — short cycles, real lesson plans, real standards tags.",
  "Beta-test features publicly and privately with the team — flag what works, what breaks, and what would change your Monday morning.",
  "Build out the foundational 3D-lesson database by subject + grade — the library that other educators inherit.",
  "Co-author prompting guides and instructional patterns so any teacher can ship a useful lesson on day one.",
];
const RUN = [
  "The cohort community lives on Skool — a separate platform for working groups and async threads. This page is the application funnel only.",
  "~20–30 educators for the first cohort, organized into small working groups by subject area.",
  "12-week runway with a 30-minute weekly sync cadence; the bulk of work is async.",
  "Free during beta. Founding members are acknowledged in the lesson library wherever their work ships.",
];
const ROYALTY = [
  "Lessons you build during the cohort remain yours — you keep authorship and reuse rights in your own classroom.",
  "When VividVR licenses a cohort-built lesson (or a clear derivative) to a district, you get a royalty share of the license fee.",
  "The royalty percentage is set per cohort and disclosed in writing before the cohort starts — no surprises after.",
  "The basis for distribution is transparent: source lesson id, district, license period. You'll see what shipped and what it earned.",
];
const GET = [
  { icon: Rocket, title: "Early access to every feature", desc: "Use new VividVR capabilities the week they ship — no waiting on the standard rollout." },
  { icon: MessageSquare, title: "Direct input on the roadmap", desc: "A monthly call with the product team. What you flag moves the platform before it scales." },
  { icon: Users, title: "Public credit on shipped lessons", desc: "Every lesson you ship shows your name on the lesson page — authorship that travels with the work." },
  { icon: Coins, title: "Royalty share on district licenses", desc: "When a district licenses a lesson you build, you earn a share of the fee — paid quarterly with a transparent ledger." },
];
const ASK = [
  { title: "~3 hours/week for 12 weeks", desc: "Mostly async on your schedule; one 30-minute weekly sync with your subject-area team." },
  { title: "Generate at least one standards-tagged lesson", desc: "One published, CCSS/NGSS-tagged VR lesson that other educators can adopt." },
  { title: "Beta-test features publicly and privately", desc: "Some sessions are recorded (with consent); some are private threads. Both feed what we ship next." },
  { title: "Share feedback in async threads", desc: "Short written posts: what worked, what broke, what would change your Monday morning." },
];
const STATS = [
  { n: "25", l: "Founding seats total" },
  { n: "10–30", l: "License seats per tier" },
  { n: "12", l: "Weeks runway" },
  { n: "~3 hr", l: "Per week commitment" },
];
const TIERS = [
  { name: "Founding", price: "$199", desc: "The founding cohort membership: classroom licensing, unlimited generations, early-access features, and the private founding community — pricing locked in.",
    features: ["Unlimited VR lesson builds", "Classroom license — 30 students", "Founding community access", "Early-access features", "Locked-in founding pricing"], highlight: true },
];

export default function Founding() {
  const nav = useNavigate();
  const apply = () => nav("/register");
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* Hero */}
      <section className="hero-noise">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center animate-in-up">
          <Badge className="bg-accent/10 text-accent border-0 mb-4">Founding Cohort · Limited to ~30 seats</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            VividVR Founding Educator Cohort
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We're inviting a small group of K–12 educators into a hands-on beta where they generate VR lessons, build a
            shared library, and shape how VividVR ships to schools. Cohort membership bundles classroom licenses,
            unlimited generations, the premium feature set, and a private working community. The AI scene generator itself
            stays free for any classroom.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="founding-apply-hero-btn" onClick={apply} className="rounded-full h-12 px-8">Apply to join <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <Button size="lg" variant="outline" onClick={() => nav("/standards")} className="rounded-full h-12 px-8">See standards coverage</Button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16 space-y-20">
        {/* Tiers */}
        <section>
          <h2 className="text-3xl font-display font-bold tracking-tight text-center">One founding membership.</h2>
          <p className="text-center text-muted-foreground mt-3 max-w-2xl mx-auto">
            Cohort membership is paid; the AI scene generator itself stays free for any classroom. Founding locks in classroom licensing, unlimited generations, early-access features, and the founding community.
          </p>
          <div className="grid sm:grid-cols-1 gap-6 mt-10 max-w-md mx-auto">
            {TIERS.map((t) => (
              <div key={t.name} data-testid={`founding-tier-${t.name.toLowerCase()}`} className={`relative rounded-3xl border p-7 ${t.highlight ? "border-primary ring-2 ring-primary/20 shadow-2xl shadow-primary/15" : "border-border"} bg-card`}>
                {t.highlight && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground border-0">Founding member</Badge>}
                <h3 className="font-display font-bold text-xl">{t.name}</h3>
                <div className="mt-2 flex items-end gap-1"><span className="text-4xl font-display font-extrabold">{t.price}</span><span className="text-muted-foreground mb-1">/mo</span></div>
                <p className="text-sm text-muted-foreground mt-3">{t.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}</li>)}
                </ul>
                <Button onClick={apply} className={`mt-6 w-full rounded-full ${t.highlight ? "" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>Apply with {t.name}</Button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-5">Recurring monthly — cancel anytime. Applications are reviewed before any charge starts. A family single-seat plan ($19.99/mo) is available separately; cohort members get classroom licensing bundled.</p>
        </section>

        {/* What you'll do + How it runs */}
        <section className="grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-display font-bold tracking-tight mb-5">What you'll do</h2>
            <ul className="space-y-3">{DO.map((d, i) => <li key={i} className="flex gap-3 text-sm text-muted-foreground"><span className="text-primary font-bold">›</span> {d}</li>)}</ul>
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold tracking-tight mb-5">How it runs</h2>
            <ul className="space-y-3">{RUN.map((d, i) => <li key={i} className="flex gap-3 text-sm text-muted-foreground"><span className="text-accent font-bold">›</span> {d}</li>)}</ul>
          </div>
        </section>

        {/* Revenue share */}
        <section className="rounded-3xl border border-border bg-card p-8 sm:p-10">
          <div className="flex items-center gap-2 mb-5"><Coins className="w-6 h-6 text-amber-600" /><h2 className="text-2xl font-display font-bold tracking-tight">Revenue share & royalty terms</h2></div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {ROYALTY.map((r, i) => <div key={i} className="flex gap-3 text-sm text-muted-foreground"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {r}</div>)}
          </div>
        </section>

        {/* What you get / what we ask */}
        <section className="grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-display font-bold tracking-tight mb-6">What you get</h2>
            <div className="space-y-4">
              {GET.map((g) => (
                <div key={g.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><g.icon className="w-5 h-5" /></div>
                  <div><div className="font-semibold text-sm">{g.title}</div><div className="text-sm text-muted-foreground">{g.desc}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold tracking-tight mb-6">What we ask</h2>
            <div className="space-y-4">
              {ASK.map((a) => (
                <div key={a.title} className="rounded-xl border border-border p-4">
                  <div className="font-semibold text-sm">{a.title}</div><div className="text-sm text-muted-foreground mt-1">{a.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.l} className="rounded-2xl bg-secondary p-6 text-center">
              <div className="text-3xl font-display font-extrabold text-primary">{s.n}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </section>

        {/* Testimonial */}
        <section className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14">
          <Quote className="w-9 h-9 text-accent" />
          <p className="text-xl sm:text-2xl font-display font-semibold leading-snug mt-4 max-w-3xl">
            “The first time I generated a whole VR field trip for my biology class in an afternoon, I knew this was going to
            change how I teach cell structure next year. I want to be in the room where this gets built.”
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center font-bold">RP</div>
            <div><div className="font-semibold">Renée Park</div><div className="text-white/70 text-sm">High school biology teacher · Cohort I applicant</div></div>
          </div>
        </section>

        {/* Apply CTA */}
        <section className="text-center">
          <Badge className="bg-primary/10 text-primary border-0 mb-3">Cohort I · 25 seats</Badge>
          <h2 className="text-3xl font-display font-bold tracking-tight">Applications reviewed in the order they arrive.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Pick a cohort tier ($99 or $199/mo, cancel anytime) and apply — the form takes five short fields. We review every
            application personally and reply within a few days. Once accepted, you'll be invited to the Skool community.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="founding-apply-btn" onClick={apply} className="rounded-full h-12 px-8">Apply to join <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <Button size="lg" variant="outline" onClick={() => nav("/pricing")} className="rounded-full h-12 px-8">See pricing</Button>
          </div>
          <p className="text-sm text-muted-foreground mt-5">
            Questions before you apply? Email <a href="mailto:hello@vividvrlessons.com" className="text-primary font-semibold">hello@vividvrlessons.com</a> — we read every message.
          </p>
        </section>
      </div>
    </div>
  );
}
